import React, { useState, useEffect } from 'react';
import { logout } from '../lib/auth';
import { Button } from '@mes-amis/galette-design-system';
import { getDaysSinceLastOnline, updateLastOnlineTime } from '../db/services/offline_time_tracker';
import { OFFLINE_LIMIT_DAYS } from '../config/env';
import { createLogger } from '../lib/logger';
import { ExclamationTriangleIcon, ClockIcon } from '@heroicons/react/24/outline';

const logger = createLogger('OfflineExpired');

/**
 * OfflineExpired Page - Shown when user has been offline for more than 30 days
 * Blocks access to all app features until they go back online
 * Provides a logout option to clear data
 */
export default function OfflineExpired() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [logoutConfirmation, setLogoutConfirmation] = useState('');
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [daysSinceOnline, setDaysSinceOnline] = useState<number | null>(null);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
      logger.log('Connection restored - updating timestamp before redirect...');
      // Update the last online timestamp BEFORE redirecting to prevent
      // a race condition where AuthWrapper reads a stale expired timestamp
      await updateLastOnlineTime();
      window.location.href = '/';
    };

    const handleOffline = () => {
      setIsOnline(false);
      logger.log('Still offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load days since last online
  useEffect(() => {
    getDaysSinceLastOnline().then(days => {
      setDaysSinceOnline(days);
    }).catch(err => {
      logger.error('Failed to get days since last online:', err);
    });
  }, []);

  const handleLogout = async () => {
    if (logoutConfirmation.toLowerCase() !== 'logout and wipe device') {
      return;
    }

    setIsLoggingOut(true);
    await logout();
  };

  return (
    <div className="h-full bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 p-8 text-white">
          <div className="flex items-center justify-center mb-4">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <ExclamationTriangleIcon className="w-12 h-12" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-center mb-2">Offline Time Limit Exceeded</h1>
          <p className="text-center text-white/90 text-lg">
            You've been offline for more than {OFFLINE_LIMIT_DAYS} {OFFLINE_LIMIT_DAYS === 1 ? 'day' : 'days'}
          </p>
        </div>

        {/* Content Section */}
        <div className="p-8">
          {/* Status Info */}
          <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <ClockIcon className="w-6 h-6 text-orange-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-orange-900 mb-2">
                  Access Restricted
                </h2>
                <p className="text-orange-800 text-sm mb-2">
                  For security and data integrity reasons, access to the application is blocked after {OFFLINE_LIMIT_DAYS} {OFFLINE_LIMIT_DAYS === 1 ? 'day' : 'days'} offline.
                </p>
                {daysSinceOnline !== null && (
                  <p className="text-orange-900 text-sm font-medium">
                    Last online: <span className="font-bold">{daysSinceOnline} days ago</span>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Online Status */}
          <div className={`rounded-lg p-4 mb-6 ${isOnline ? 'bg-green-50 border-2 border-green-200' : 'bg-gray-50 border-2 border-gray-200'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${isOnline ? 'bg-green-500' : 'bg-gray-400'} ${!isOnline && 'animate-pulse'}`}></div>
              <div>
                <p className={`font-medium ${isOnline ? 'text-green-900' : 'text-gray-700'}`}>
                  {isOnline ? 'Connection Restored!' : 'You are currently offline'}
                </p>
                <p className={`text-sm ${isOnline ? 'text-green-700' : 'text-gray-600'}`}>
                  {isOnline
                    ? 'Redirecting to the application...'
                    : 'Please connect to the internet to regain access'}
                </p>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="space-y-4 mb-6">
            <h3 className="font-semibold text-gray-900">To regain access:</h3>
            <ol className="space-y-3 text-gray-700">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-sm font-semibold">
                  1
                </span>
                <span>Connect your device to the internet (WiFi or cellular data)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-sm font-semibold">
                  2
                </span>
                <span>Wait for the connection to be established</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-sm font-semibold">
                  3
                </span>
                <span>The app will automatically redirect you once online</span>
              </li>
            </ol>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 my-6"></div>

          {/* Logout Section */}
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-red-900 mb-2">
                Logout and Wipe Device
              </h3>
              <p className="text-sm text-red-800 mb-2">
                If you want to start fresh, you can log out and clear all local data.
              </p>
              <p className="text-sm text-red-800 font-medium">
                ⚠️ Warning: This will permanently delete all offline data from this device.
              </p>
            </div>

            <div className="space-y-3">
              <label htmlFor="logout-confirmation" className="block text-sm font-medium text-red-900">
                Type <code className="bg-red-100 px-2 py-0.5 rounded font-mono text-red-900">logout and wipe device</code> to confirm:
              </label>
              <div className="flex gap-3">
                <input
                  id="logout-confirmation"
                  type="text"
                  value={logoutConfirmation}
                  onChange={(e) => setLogoutConfirmation(e.target.value)}
                  placeholder="Type 'logout and wipe device' to confirm"
                  disabled={isLoggingOut}
                  className="flex-1 px-4 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <Button
                  variant="primary"
                  destructive
                  onClick={handleLogout}
                  disabled={logoutConfirmation.toLowerCase() !== 'logout and wipe device' || isLoggingOut}
                >
                  {isLoggingOut ? 'Logging out...' : 'Logout and Wipe Device'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
